import AppConfig from '@/config';

/**
 * Fluent builder for GA4 events.
 *
 * const payload = new GaEvent('membership_tier_viewed')
 *   .addParam('membership_tier', 'enthusiast')
 *   .toPayload()
 *
 * event(payload.event, payload.params)
 */
const MAX_PARAMS = 25;
const MAX_PARAM_KEY_LENGTH = 40;

function normalizeKey(key: string | null) {
  if (key === null) {
    return null;
  }

  return String(key)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // camelCase -> camel_Case
    .replace(/[\s-]+/g, '_') // spaces/hyphens -> underscore
    .replace(/[^a-zA-Z0-9_]/g, '') // strip anything else invalid
    .toLowerCase();
}

const MAX_PARAM_VALUE_STR_LENGTH = 100;

function limitStringValue(value: string) {
  if (value.length > MAX_PARAM_VALUE_STR_LENGTH) {
    return value.slice(0, MAX_PARAM_VALUE_STR_LENGTH - 1).trim() + '…';
  }

  return value;
}

function normalizePathValue(path: string) {
  if (typeof path !== 'string') {
    return null;
  }

  if (path === '/') {
    return 'home_page';
  }

  const normalizedValue = path
    .toLowerCase()
    .replace(/^\//, '')
    .replace(/\/$/, '')
    .replace(/\//g, '_')
    .replace(/[^a-zA-Z0-9-_]/g, '');

  return normalizedValue || null;
}

function normalizeValue(value: any) {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value
    .replace(/<[^>]*>/g, ' ') // remove HTML tags
    .replace(/\s+/g, ' ')
    .trim();

  return limitStringValue(normalizedValue) || null;
}

/**
 * Joins an array of values with "|", splitting into
 * numbered chunk params if the joined string exceeds GA's 100-char limit.
 * e.g. { filter_destinations: "..." } or
 *      { filter_destinations_1: "...", filter_destinations_2: "..." }
 */
function buildJoinedParam(paramName: string, values: any[]) {
  const result: Record<string, any> = {};

  if (!values || values.length === 0) {
    return result;
  }

  const normalizedValues = values.map((item) => normalizeValue(item));
  const joined = normalizedValues.join('|');

  if (joined.length <= MAX_PARAM_VALUE_STR_LENGTH) {
    result[paramName] = joined;

    return result;
  }

  // Chunk: greedily pack values into pipe-joined strings <= 100 chars
  let chunkIndex = 1;
  let current: number | string | null = '';

  for (const value of normalizedValues) {
    const candidate: number | string | null = current ? `${current}|${value}` : value;

    if (typeof candidate === 'string' && candidate.length > MAX_PARAM_VALUE_STR_LENGTH) {
      if (current) {
        result[`${paramName}_${chunkIndex}`] = current;
        chunkIndex++;
      }
      // handle a single value that alone exceeds 100 chars (truncate as fallback)
      current = typeof value === 'string' ? limitStringValue(value) : value;
    } else {
      current = candidate;
    }
  }

  if (current) {
    result[`${paramName}_${chunkIndex}`] = current;
  }

  return result;
}

function showConsoleWarning(eventName: string, message: string) {
  if (AppConfig.get('isLocal')) {
    console.group(`\n%cIGNORING [GA EVENT] - ${eventName}`, 'padding: 2px 6px; border-radius: 3px; font-weight: bold;');
    console.warn(`REASON - ${message}.`);
    console.groupEnd();
    /* eslint-enable no-console */
  }
}

export default class GaEvent {
  // Shared across all instances — updated once per navigation
  static sourcePage: string | null = null;
  private readonly name: string;
  private params: Record<string, any>;

  /** Call this once per route change (e.g., in router.afterEach) */
  static setSourcePage(path: string) {
    if (path) {
      GaEvent.sourcePage = normalizePathValue(path);
    } else {
      GaEvent.sourcePage = null;
    }
  }

  static isSourcePageSet() {
    return GaEvent.sourcePage !== null;
  }

  static fromPlainObject({ name, params = {} }: { name: string; params?: {} }) {
    return new GaEvent(name, { ...params });
  }

  constructor(name: string, params = {}) {
    if (!name) {
      throw new Error('GaEvent: event name is required');
    }

    this.name = name;
    this.params = {
      source_page: GaEvent.sourcePage,
    };
    this.addParams(params);
  }

  clone() {
    return new GaEvent(this.name, { ...this.params });
  }

  addParam(key: string, value: any) {
    if ([undefined, null].includes(value)) {
      return this;
    }

    const normalizedKey = normalizeKey(key);

    if (!normalizedKey || normalizedKey.length > MAX_PARAM_KEY_LENGTH) {
      showConsoleWarning(this.name, `invalid param key "${key}"`);

      return this;
    }

    // Only block truly new params — updating an existing key doesn't grow the count
    if (!(normalizedKey in this.params) && Object.keys(this.params).length >= MAX_PARAMS) {
      showConsoleWarning(this.name, `param limit of ${MAX_PARAMS} reached`);

      return this;
    }

    let updatedParams: Record<string, any> = {
      ...this.params,
    };

    if (Array.isArray(value)) {
      updatedParams = {
        ...updatedParams,
        ...buildJoinedParam(normalizedKey, value),
      };
    } else {
      updatedParams[normalizedKey] = normalizeValue(value);
    }

    if (Object.keys(updatedParams).length >= MAX_PARAMS) {
      showConsoleWarning(this.name, `param limit of ${MAX_PARAMS} reached`);

      return this;
    }

    this.params = updatedParams;

    return this;
  }

  addParams(params = {}) {
    Object.entries(params).forEach(([key, value]) => {
      this.addParam(key, value);
    });

    return this;
  }

  removeParam(key: string) {
    delete this.params[key];

    return this;
  }

  hasParam(key: string) {
    return key in this.params && (this.params[key] !== null || this.params[key] !== undefined);
  }

  toPayload() {
    return {
      name: this.name,
      params: { ...this.params },
    };
  }

  addSourcePage(value: string) {
    if (value) {
      GaEvent.sourcePage = normalizePathValue(value);
      this.addParam('source_page', GaEvent.sourcePage);
    }

    return this;
  }

  addContentId(value: number | string) {
    this.addParam('content_id', String(value));

    return this;
  }

  addContentType(value: string) {
    this.addParam('content_type', value);

    return this;
  }

  addContentName(value: string) {
    this.addParam('content_name', value);

    return this;
  }

  addCtaName(value: string) {
    this.addParam('cta_name', value);

    return this;
  }

  hasCtaName() {
    return this.hasParam('cta_name');
  }

  addPageSection(value: string) {
    this.addParam('page_section', value);

    return this;
  }

  addPageHash(value: string) {
    this.addParam('page_hash', normalizePathValue(value));

    return this;
  }

  addPageSlug(value: string) {
    this.addParam('page_slug', normalizePathValue(value));

    return this;
  }

  addPathParam(key: string, value: string) {
    this.addParam(key, normalizePathValue(value));

    return this;
  }

  addPartnerName(value: string) {
    this.addParam('partner_name', value);

    return this;
  }

  addBookingType(value: string) {
    this.addParam('booking_type', value);

    return this;
  }

  addApplicationType(value: string) {
    this.addParam('application_type', value);

    return this;
  }

  addMembershipTier(value: string) {
    this.addParam('membership_tier', value);

    return this;
  }

  addMembershipPlanName(value?: string) {
    this.addParam('membership_plan_name', value);

    return this;
  }

  addStepName(name?: string) {
    return this.addParam('step_name', name);
  }

  addStepNumber(index: number | string) {
    return this.addParam('step_number', index);
  }

  addMemberStatus(membership: string | null) {
    return this.addParam('member_status', membership);
  }
}
